import type { Media, Task } from 'src/services/apollo/generated/graphql'
import { getImageFileDimension } from 'src/utils/imageUtil'
import { track as trackEvent } from 'src/utils/tracker'
import {
  type EditorAsset,
  type EditorStore,
  type GenMediaShape,
  type ImageShape,
  createAssetId,
  createShapeId,
} from '../editor-core'
import { generateAssetIdFromMedia } from './assets'

export type FillEditorOp = (store: EditorStore) => void | Promise<void>

export type FillBaseImageImportArgs = {
  media: Media
  task?: Task
  category?: string
}

export function createFillEditorContextUtilsV2() {
  let store: EditorStore | null = null
  const opQueue: FillEditorOp[] = []

  const queueOp = (op: FillEditorOp) => {
    if (store) {
      Promise.resolve(op(store)).catch(console.error)
    } else {
      opQueue.push(op)
    }
  }

  const setBaseImageFromMedia = (
    media: Media,
    { task, category }: { task?: Task; category?: string } = {},
  ) =>
    queueOp(store => {
      const shapeId = createShapeId()
      const assetId = generateAssetIdFromMedia(store, media)

      store.clearPage()
      store.createShape<GenMediaShape>({
        id: shapeId,
        type: 'genmedia',
        x: 0,
        y: 0,
        props: {
          w: media.width ?? 0,
          h: media.height ?? 0,
          prompts: task?.parameters.prompts || null,
          currentAssetId: assetId,
          assetIds: [assetId],
          pending: null,
        },
        meta: {
          generationTask: task ?? null,
          pixai_isBaseImage: true,
        },
      })
      store.select(shapeId)
      store.zoomToBounds(store.getShapeBounds(store.getShape(shapeId)!))

      trackEvent('panel_image_import', {
        scene: 'fill',
        category,
      })
    })

  const setBaseImageFromFile = (
    file: File,
    { category }: { category?: string } = {},
  ) =>
    queueOp(async store => {
      const shapeId = createShapeId()

      const dim = await getImageFileDimension(file)

      // clearPage first so shapes no longer reference the old assets,
      // then deleteAssets revokes stale blob URLs.
      store.clearPage()
      store.deleteAssets([...store.assets.keys()])

      const objectUrl = URL.createObjectURL(file)
      const assetId = createAssetId()
      const asset: EditorAsset = {
        id: assetId,
        name: file.name,
        src: objectUrl,
        width: dim?.width ?? 0,
        height: dim?.height ?? 0,
        mimeType: file.type || 'image/png',
        meta: {
          originalWidth: dim?.width,
          originalHeight: dim?.height,
        },
      }
      store.createAsset(asset)
      store.createShape<ImageShape>({
        id: shapeId,
        type: 'image',
        x: 0,
        y: 0,
        props: {
          w: asset.width,
          h: asset.height,
          assetId: asset.id,
        },
        meta: {
          pixai_isBaseImage: true,
        },
      })
      store.select(shapeId)
      store.zoomToBounds(store.getShapeBounds(store.getShape(shapeId)!))

      trackEvent('panel_image_import', {
        scene: 'fill',
        category,
      })
    })

  return {
    get editor() {
      return store
    },
    set editor(newStore: EditorStore | null) {
      if (store === newStore) return
      store = newStore
      if (store) {
        const pending = opQueue.splice(0)
        for (const op of pending) {
          Promise.resolve(op(store)).catch(console.error)
        }
      }
    },

    queueOp,
    setBaseImageFromMedia,
    setBaseImageFromFile,
  }
}

export type FillEditorContextUtilsV2 = ReturnType<
  typeof createFillEditorContextUtilsV2
>
